import { Button } from '@components/Button'
import { useNetwork } from '@hooks/useNetwork'
import { useChannelReply } from '@services/api/graphql/mutations/channelReply'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextArea, XStack, YStack } from 'tamagui'

type ToastType = 'success' | 'error'

interface IMessageReplyFormProps {
  channelId: string
  onReplied?: () => void
  onToast?: (type: ToastType, message: string) => void
}

export function MessageReplyForm({
  channelId,
  onReplied,
  onToast,
}: IMessageReplyFormProps) {
  const { t } = useTranslation('Messages')
  const { isConnected } = useNetwork()
  const { handleReply, loading } = useChannelReply()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async () => {
    if (!text.trim()) {
      setError(t('reply.validations.textRequired'))
      return
    }

    if (!isConnected) {
      onToast?.('error', t('reply.offline'))
      return
    }

    try {
      const result = await handleReply({ channelId, text: text.trim() })
      if (result?.errors && result.errors.length > 0) {
        onToast?.('error', result.errors[0])
        return
      }
      setText('')
      setError(null)
      onReplied?.()
      onToast?.('success', t('reply.success'))
    } catch {
      onToast?.('error', t('reply.error'))
    }
  }

  return (
    <YStack gap="$spacing-inset-xs" testID="message-reply-form">
      <TextArea
        value={text}
        onChangeText={(value) => {
          setText(value)
          if (error) setError(null)
        }}
        placeholder={t('reply.placeholder')}
        borderColor={error ? '$danger-300' : '$border-default'}
        backgroundColor="$bg-input"
        fontFamily="$mulishRegular"
        fontSize="$font-size-xxs"
        color="$text-primary"
        placeholderTextColor="$text-placeholder"
        minHeight={80}
        testID="message-reply-form-text"
      />
      <XStack justifyContent="flex-end">
        <Button
          variant="primary"
          size="small"
          onPress={onSubmit}
          disabled={loading}
          loading={loading}
          testID="message-reply-form-submit"
        >
          {t('reply.submit')}
        </Button>
      </XStack>
    </YStack>
  )
}
